import React, { useState } from "react";
import { parseISO, startOfToday, addDays } from "date-fns";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useApp } from "@/context/AppContext";
import { toISO, fmtDate, rentalDays, dateLocale, MAX_DAYS } from "@/lib/dates";

export function DateRangePicker({ start, end, onChange, testid, compact = false }) {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const selected = { from: start ? parseISO(start) : undefined, to: end ? parseISO(end) : undefined };
  const days = rentalDays(start, end);

  const handleSelect = (range) => {
    const from = range?.from;
    const to = range?.to;
    if (!from) return onChange(null, null);
    onChange(toISO(from), to ? toISO(to) : null);
    if (to) setOpen(false);
  };

  const label = start && end
    ? `${fmtDate(start, lang)} → ${fmtDate(end, lang)}`
    : start ? `${fmtDate(start, lang)} → …` : t.dates.choose;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testid}-trigger`}
          className={`flex w-full items-center gap-2 rounded-xl border text-left transition-colors hover:border-cea-accent ${start && end ? "border-slate-200 bg-white" : "border-cea-accent/50 bg-blue-50/40"} ${compact ? "px-2.5 py-1.5 text-xs" : "px-4 py-3 text-sm"}`}
        >
          <CalendarDays className={`${compact ? "h-3.5 w-3.5" : "h-4 w-4"} shrink-0 text-cea-accent`} />
          <span className="flex-1 font-medium text-cea-ink">{label}</span>
          {days > 0 && <span className="font-semibold text-cea-navy" data-testid={`${testid}-days`}>{days} {days > 1 ? t.dates.days : t.dates.day}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start" data-testid={`${testid}-calendar`}>
        <Calendar
          mode="range"
          selected={selected}
          onSelect={handleSelect}
          locale={dateLocale(lang)}
          disabled={[{ before: startOfToday() }, ...(start && !end ? [{ after: addDays(parseISO(start), MAX_DAYS - 1) }] : [])]}
          defaultMonth={selected.from || startOfToday()}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}
